// External packages
import * as React from 'react';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Utils
import { twMerge } from 'tailwind-merge';

const GripIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
    {...props}
  >
    <circle cx="7" cy="5" r="1.5" />
    <circle cx="7" cy="10" r="1.5" />
    <circle cx="7" cy="15" r="1.5" />
    <circle cx="13" cy="5" r="1.5" />
    <circle cx="13" cy="10" r="1.5" />
    <circle cx="13" cy="15" r="1.5" />
  </svg>
);

/**
 * Reusable drag-and-drop sortable list.
 *
 * Designed to be reused for any list where the user needs to control display
 * order: exhibits within a place, images, etc. Stays uncontrolled — the parent
 * supplies the ordered array and is notified with the new order on every drop.
 *
 * Each item must expose a stable string `id`.
 *
 * Example:
 *   <SortableList
 *     items={exhibits}
 *     onChange={setExhibits}
 *     renderItem={(exhibit) => <ExhibitRow exhibit={exhibit} />}
 *   />
 */
export type SortableListProps<T extends { id: string }> = {
  items: T[];
  onChange: (next: T[]) => void;
  renderItem: (item: T, meta: { index: number; isDragging: boolean }) => React.ReactNode;
  isDisabled?: boolean;
  className?: string;
  itemClassName?: string;
};

export function SortableList<T extends { id: string }>({
  items,
  onChange,
  renderItem,
  isDisabled,
  className,
  itemClassName,
}: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((it) => it.id === active.id);
    const newIndex = items.findIndex((it) => it.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    onChange(arrayMove(items, oldIndex, newIndex));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((i) => i.id)}
        strategy={verticalListSortingStrategy}
        disabled={isDisabled}
      >
        <ul className={twMerge('flex flex-col gap-2', className)}>
          {items.map((item, index) => (
            <SortableRow
              key={item.id}
              id={item.id}
              isDisabled={isDisabled}
              className={itemClassName}
            >
              {(meta) => renderItem(item, { ...meta, index })}
            </SortableRow>
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

type SortableRowProps = {
  id: string;
  isDisabled?: boolean;
  className?: string;
  children: (meta: { isDragging: boolean }) => React.ReactNode;
};

const SortableRow: React.FC<SortableRowProps> = ({
  id,
  isDisabled,
  className,
  children,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: isDisabled });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={twMerge(
        'flex items-stretch gap-2 rounded-lg border border-gray-200 bg-white',
        isDragging && 'shadow-lg ring-2 ring-blue-300',
        className
      )}
    >
      <button
        type="button"
        aria-label="Premjesti"
        disabled={isDisabled}
        className={twMerge(
          'flex w-10 shrink-0 items-center justify-center rounded-l-lg border-r border-gray-200 text-gray-400 transition-colors',
          isDisabled
            ? 'cursor-not-allowed'
            : 'cursor-grab hover:bg-gray-50 hover:text-gray-600 active:cursor-grabbing'
        )}
        {...attributes}
        {...listeners}
      >
        <GripIcon className="size-5" />
      </button>
      <div className="min-w-0 flex-1 py-2 pr-2">{children({ isDragging })}</div>
    </li>
  );
};
